import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import Layout from "@/components/Layout";
import useLivlyUser from "@/context/UserProvider";
import {
  AssurantQuoteV2,
  DisclosureType,
  RequoteRequestModelV2,
  SelectionMetaData
} from "@/types/Insurance";
import { OnboardingStatus, OnboardingStatusEnum, User } from "@/types/User";
import React, { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import moment from "moment";
import formatCurrency from "@/utils/formatCurrency";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "@/components/Dialog";
import { textInputClass } from "@/components/Form";
import { Button } from "@/components/Button";
import { Spinner } from "@/components/Spinner";
import { isNativeAppVersion, returnToNative } from "@/utils/nativeAppHelpers";
import {
  formatPhone,
  isValidPhone,
  PHONE_FORMAT_ALTERNATE,
  stripPhone
} from "@/utils/phone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetStatusTypeDetails, usePostService } from "./landing";
import { BaseLivlyApiResponse } from "@/types/Base";
import axios from "axios";
import { BASE_API_URL } from "@/utils/constants";
import Avatar from "@/components/Avatar";
import classNames from "classnames";
import InputMask from "react-input-mask";
import Alert from "@/components/Alert";
import { useGetDisclosures } from "./checkout-insurance";
import {
  Questionnaire,
  useGetLiabilityQuestions300K,
  usePostLiabilityQuestions300K
} from "./liability-questions-300K";

const UNDERWRITERS_FALLBACK_HTML = `In all states, Renters Insurance and Residents Liability Insurance are underwritten by American Bankers Insurance    Company of Florida with its home office in Miami, Florida. In Minnesota, the underwriter is American Security    Insurance company. In Georgia,    Residents Liability is underwritten by Voyager Indemnity Insurance Company. These companies operate under the trade name Assurant.</p><p>In Utah, coverage is provided under policy form AJ8850PC-0307 or AJ9281CKK-0609.</p><p>In CA, the NAIC # is 10111.</p><p>Each insurer has sole financial responsibility for its own products.</p></div></div> </body> </html>`;

const InsuranceQuoteV2 = ({
  isInsuranceOnly
}: {
  isInsuranceOnly: boolean;
}) => {
  const { user } = useLivlyUser();
  const navigate = useNavigate();
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);

  const [assurantQuote, setAssurantQuote] = useState<AssurantQuoteV2 | null>(
    null
  );

  const {
    data,
    isError,
    selectionMetaData,
    updateMetaData,
    refetch,
    isRequire300kLiability,
    error
  } = useOutletContext<InsuranceQuoteContext>();
  const {
    data: underwritersDisclosure,
    isLoading: isUnderwritersDisclosureLoading
  } = useGetDisclosures(DisclosureType.Underwriters, user.state);
  const requoteMutationV2 = useRequoteInsuranceV2Quote(user);

  // Extract URLs for 300k liability questions if required
  let initialUrl = "";
  let outcomeUrl = "";
  if (isRequire300kLiability && data) {
    const businessRules = (data as AssurantQuoteV2)?.quoteResponse?.coveragesCue
      ?.businessRules;
    if (
      businessRules &&
      businessRules.length > 0 &&
      businessRules[0].rule300KLiability
    ) {
      initialUrl = businessRules[0].rule300KLiability.initialUrl ?? "";
      outcomeUrl = businessRules[0].rule300KLiability.outcomeUrl ?? "";
    }
  }

  // Only call the hooks if the URLs are available
  const liabilityQuestionsQuery = initialUrl
    ? useGetLiabilityQuestions300K(initialUrl)
    : { data: null, isLoading: false };
  const update300KAssurantQuote = outcomeUrl
    ? usePostLiabilityQuestions300K(outcomeUrl)
    : null;

  const post300KAnswers = async () => {
    if (!update300KAssurantQuote) return;
    const generate300KPayload = build300KLiabilityPayload(
      liabilityQuestionsQuery.data ?? null
    );
    if (generate300KPayload) {
      await update300KAssurantQuote.mutateAsync(generate300KPayload, {
        onSuccess: (data) => {
          if (data.isApproved) {
            updateMetaData({
              ...selectionMetaData!,
              uwAnswers: data?.pmsValues[0]?.value ?? ""
            });
            const options =
              isInsuranceOnly === true
                ? { state: { label: "Insurance", isInsuranceOnly: true } }
                : { state: { label: "Insurance" } };
            //pass state to navigate
            navigate(`../insurance-quote/summary`, options);
          } else {
            navigate(`../insurance-quote/not-qualified`);
          }
        },
        onError: () => {
          navigate(`../insurance-quote/not-qualified`);
        }
      });
    }
  };

  const onCallRequoteForRequire300kLiability = async (coverage: {
    liabilityAmount?: string;
  }) => {
    try {
      const v2 = (data as AssurantQuoteV2).quoteResponse;
      const quoteRequest = (data as AssurantQuoteV2).quoteRequest;
      const paramNameMap: Record<string, string> = {
        iuiTypes: "iuiType"
      };
      const updatedParameters = v2.coverageDetails.parameters.map((param) => {
        // Map parameter name if mapping exists, otherwise use original
        const mappedName = paramNameMap[param.name] || param.name;
        // If the coverage object contains a value for this parameter, update it
        if (
          coverage &&
          Object.prototype.hasOwnProperty.call(coverage, param.name)
        ) {
          return {
            ...param,
            name: mappedName,
            value:
              mappedName === "liabilityAmount"
                ? "300000"
                : (coverage as any)[param.name]
          };
        }
        // If just the name needs to be changed (iuiTypes to iuiType)
        if (param.name === "iuiTypes") {
          return {
            ...param,
            name: "iuiType"
          };
        }
        return param;
      });
      const requestV2 = {
        transaction: {
          clientId: v2.transactionDetails.clientId,
          productType: v2.transactionDetails.productId,
          workItemId: v2.transactionDetails.workItemId,
          jobNumber: quoteRequest.transaction.jobNumber,
          policyEffectiveDate: v2.transactionDetails.policyEffectiveDate,
          referralId: v2.transactionDetails.referralId
        },
        policyHolder: {
          insured: quoteRequest.policyHolder.insured
        },
        underWriting: {
          questions: quoteRequest.underWriting.questions
        },
        coverage: {
          parameters: updatedParameters
        }
      };
      await requoteMutationV2.mutateAsync(requestV2, {
        onSuccess: (data) => {
          setAssurantQuote(data as AssurantQuoteV2);
        }
      });
    } catch (e) {}
    refetch();
  };

  useEffect(() => {
    if (data) {
      setAssurantQuote(data as AssurantQuoteV2);
    }
  }, [data]);

  // Check if we need to requote for 300k liability
  useEffect(() => {
    if (data && !isError) {
      const { parameters } = (data as AssurantQuoteV2)?.quoteResponse
        .coverageDetails;
      const liabilityAmount = getValue(
        parameters,
        "liabilityAmount"
      )?.toString();
      // If the liability amount is not 300000 and we require it, requote
      if (isRequire300kLiability && liabilityAmount !== "300000") {
        onCallRequoteForRequire300kLiability({
          liabilityAmount: "300000"
        });
      }
    }
  }, [data, isRequire300kLiability, isError]);

  const onUpdateQuote = (newQuote: Partial<AssurantQuoteV2>) => {
    if (!assurantQuote) {
      return;
    }

    setAssurantQuote({ ...assurantQuote, ...newQuote });
  };

  const handleGetFreshQuote = async () => {
    await deleteAssuranceQuoteMutation.mutateAsync();
    const quote = data as AssurantQuoteV2;
    await updateAssurantQuoteMutation.mutateAsync(
      {
        newRequest: {
          ...quote.quoteRequest,
          transaction: {
            ...quote.quoteRequest.transaction,
            referralId: Math.floor(
              1000000000 + Math.random() * 9000000000
            ).toString(), // 10-digit random
            workItemId: null
          }
        },
        SelectionMetaData: { ...selectionMetaData, isPlanAdded: false }
      },
      {
        onSuccess: (data) => {
          setAssurantQuote(data);
          refetch();
        }
      }
    );
  };

  return (
    <Layout
      title="Insurance"
      back={{
        label: "Move-in Checklist",
        to: `..`
      }}
    >
      {(liabilityQuestionsQuery.isLoading || requoteMutationV2.isLoading) &&
      isRequire300kLiability ? (
        <div className="fixed inset-0 bg-gray-100 bg-opacity-50 flex flex-col items-center justify-center md:ml-64">
          <Spinner color="livly" size="xl" />
          <p className="mt-4 text-center text-gray-700">
            Your building is now configured with $300,000 liability coverage.
            Please hold while we continue...
          </p>
        </div>
      ) : isError ? (
        <Alert
          variant="danger"
          message={`Sorry, there was an issue loading your insurance quote.${
            (error as any)?.data?.Errors
              ? ` ${Object.entries((error as any)?.data?.Errors)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(", ")}`
              : ""
          }`}
        />
      ) : assurantQuote ? (
        assurantQuote?.quoteResponse.transactionDetails?.responseMessage ? (
          <InsuranceQuoteError
            user={user}
            errorMessage={
              assurantQuote.quoteResponse.transactionDetails.responseMessage
            }
            action={
              <Button
                color="primary"
                onClick={handleGetFreshQuote}
                className="flex items-center w-full gap-2 md:w-auto"
                disabled={
                  deleteAssuranceQuoteMutation.isLoading ||
                  updateAssurantQuoteMutation.isLoading
                }
              >
                {(deleteAssuranceQuoteMutation.isLoading ||
                  updateAssurantQuoteMutation.isLoading) && <Spinner />}
                Get Fresh Quote
              </Button>
            }
          />
        ) : (
          <div className="max-w-4xl mx-auto">
            <h2 className="font-medium">Instant Quote</h2>
            <p>
              Here’s a personalized plan to protect you, your stuff, and your
              apartment. Coverage will renew automatically.
            </p>
            <QuoteDetails
              user={user}
              quote={assurantQuote}
              onUpdateQuote={onUpdateQuote}
              selectionMetaData={selectionMetaData}
              updateMetaData={updateMetaData}
            />
            <div className="mt-4 divide-y divide-gray-200 divide-solid">
              <Coverages assurantQuote={assurantQuote} />
              <InsuredInformation
                user={user}
                quote={assurantQuote}
                onUpdateQuote={onUpdateQuote}
                selectionMetaData={selectionMetaData}
                refetch={refetch}
                updateMetaData={updateMetaData}
              />
              <PolicyDelivery
                user={user}
                quote={assurantQuote}
                onUpdateQuote={onUpdateQuote}
                selectionMetaData={selectionMetaData}
                updateMetaData={updateMetaData}
                refetch={refetch}
              />
            </div>

            <div className="mt-8">
              {isUnderwritersDisclosureLoading ? (
                <Spinner />
              ) : (
                <div
                  className="prose max-w-none whitespace-pre-line text-sm text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html:
                      underwritersDisclosure?.Content ??
                      UNDERWRITERS_FALLBACK_HTML
                  }}
                />
              )}
            </div>
            <UploadProof />
            <PlanActions
              quote={assurantQuote}
              isInsuranceOnly={isInsuranceOnly}
              selectionMetaData={selectionMetaData}
              updateMetaData={updateMetaData}
              refetch={refetch}
              isRequire300kLiability={isRequire300kLiability}
              post300KAnswers={post300KAnswers}
            />
          </div>
        )
      ) : null}
    </Layout>
  );
};

function InsuranceQuoteError({
  user,
  errorMessage,
  action
}: {
  user: User;
  errorMessage: string;
  action?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const details = useGetStatusTypeDetails(OnboardingStatusEnum.insurance, user);
  const { skipOnboardingStep, isLoading, status } = usePostService(
    user.userId,
    user.propertyUnitLeaseId.toString(),
    details.onboardingStatus
  );

  const onSkip = async () => {
    await skipOnboardingStep();

    if (isNativeAppVersion()) {
      returnToNative();
      return;
    }

    navigate("..");
  };

  return (
    <div className="flex flex-col items-center justify-center gap-8">
      <p className="text-red-400">{errorMessage}</p>
      <div className="flex justify-between space-x-2">
        {action}
        <Button
          color="default"
          onClick={onSkip}
          className="flex items-center w-full gap-2 md:w-auto"
          disabled={isLoading}
        >
          {status === "skipping" && <Spinner />}
          Skip
        </Button>
      </div>
    </div>
  );
}

function InsuredInformation({
  user,
  quote,
  onUpdateQuote,
  selectionMetaData,
  refetch,
  updateMetaData
}: {
  user: User;
  quote: AssurantQuoteV2;
  onUpdateQuote: (newQuote: Partial<AssurantQuoteV2>) => void;
  selectionMetaData: SelectionMetaData | null;
  refetch: () => void;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
}) {
  return (
    <div className="py-4">
      <h3 className="font-medium">Insured Information</h3>
      <div className="flex items-center justify-between mt-4">
        <div>
          <p className="text-sm font-light text-gray-700">insured name</p>
          <p className="mt-1">
            {user.firstName} {user.lastName}
          </p>
        </div>
        <Avatar
          size="sm"
          src={user.avatarURI}
          name={`${user.firstName} ${user.lastName}`}
        />
      </div>
      {user.roommates && user.roommates?.length > 0 ? (
        <AdditionallyInsured
          user={user}
          quote={quote}
          onUpdateQuote={onUpdateQuote}
          selectionMetaData={selectionMetaData}
          refetch={refetch}
          updateMetaData={updateMetaData}
        />
      ) : null}
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">insured address</p>
        <p className="mt-1">
          {user.buildingName} {user.unit}
        </p>
        <p className="text-sm font-light">{user.address}</p>
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">phone number</p>
        {user.unformattedPhone ? (
          <p className="mt-1">
            {formatPhone(user.unformattedPhone, PHONE_FORMAT_ALTERNATE)}
          </p>
        ) : (
          <AddPhoneNumber />
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">email address</p>
        <p className="mt-1">{user.email}</p>
      </div>
    </div>
  );
}

function getValue(
  data: any[],
  paramName: string
): string | number | number[] | null | undefined {
  return data.find((p) => p.name === paramName)?.value;
}

function Coverages({ assurantQuote }: { assurantQuote: AssurantQuoteV2 }) {
  const { parameters } = assurantQuote.quoteResponse.coverageDetails;
  const liabilityAmount = getValue(parameters, "liabilityAmount");
  const personalPropertyAmount = getValue(parameters, "personalPropertyAmount");
  const hurricaneDeductible = getValue(parameters, "hurricaneDeductible");
  const deductible = getValue(parameters, "deductible");
  const coverageSewerBackup = getValue(parameters, "waterBackup");
  const coverageFraudExpense = getValue(parameters, "identityFraud");

  const earthquake = getValue(parameters, "earthquake");
  const { state } =
    assurantQuote.quoteResponse.policyHolderDetails.insured.address;
  return (
    <div className="py-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">My Coverages</h3>

        <Link
          className="font-medium text-red-400 hover:underline"
          to="coverages"
        >
          Edit Coverage
        </Link>
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">personal liability</p>
        <p className="mt-1">{formatCurrency(Number(liabilityAmount))}</p>
      </div>

      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">personal belongings</p>
        <p className="mt-1">{formatCurrency(Number(personalPropertyAmount))}</p>
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">
          deductible {hurricaneDeductible ? "(all other perils)" : ""}
        </p>
        <p className="mt-1">{formatCurrency(Number(deductible))}</p>
      </div>
      {hurricaneDeductible && state === "FL" && (
        <div className="mt-4">
          <p className="text-sm font-light text-gray-700">
            hurricane deductible
          </p>
          <p className="mt-1">{formatCurrency(Number(hurricaneDeductible))}</p>
        </div>
      )}
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">replacement coverage</p>
        <p className="mt-1">included</p>
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">water/sewer back up</p>
        <p className="mt-1">
          {coverageSewerBackup === "Y" ? "included" : "excluded"}
        </p>
      </div>
      <div className="mt-4">
        <p className="text-sm font-light text-gray-700">
          fraud expense protection
        </p>
        <p className="mt-1">
          {coverageFraudExpense === "Y" ? "included" : "excluded"}
        </p>
      </div>

      {(state === "WA" || state === "CA") && (
        <div className="mt-4">
          <p className="text-sm font-light text-gray-700">earthquake</p>
          <p className="mt-1">{earthquake === "Y" ? "included" : "excluded"}</p>
        </div>
      )}
      {state === "NY" && (
        <div className="mt-4">
          <p className="text-sm font-light text-gray-700">flood</p>
          <p className="mt-1">included</p>
        </div>
      )}
    </div>
  );
}

function QuoteDetails({
  user,
  quote,
  onUpdateQuote,
  selectionMetaData,
  updateMetaData
}: {
  user: User;
  quote: AssurantQuoteV2;
  onUpdateQuote: (newQuote: Partial<AssurantQuoteV2>) => void;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
}) {
  const [open, setOpen] = useState(false);
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);

  const selectedPlan = getSelectedPlan(quote);

  const isFullPay = selectedPlan && selectedPlan.numberOfPayments === 1;
  const isQuarterlyPay = selectedPlan && selectedPlan.numberOfPayments === 4;
  const isEffectiveDateInPast =
    quote &&
    moment().diff(
      quote.quoteResponse.transactionDetails.policyEffectiveDate,
      "days"
    ) > 0;

  const defaultStartDate = `${moment(
    quote.quoteResponse.transactionDetails.policyEffectiveDate
  ).format("yyyy-MM-DD")}`;
  //Can’t be more than 60 days in the future.
  const maximum = moment().add(60, "days");
  const maxDate = moment(user.leaseEndDate);
  const max = maximum.isBefore(maxDate) ? maximum : maxDate;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const policyEffectiveDateString = formData.get(
      "policyEffectiveDate"
    ) as string;

    try {
      await deleteAssuranceQuoteMutation.mutateAsync();
      const policyEffectiveDate = moment(policyEffectiveDateString).format(
        "yyyy-MM-DD"
      );

      //@ts-ignore
      updateMetaData({
        ...selectionMetaData,
        isPlanAdded: false
      });

      await updateAssurantQuoteMutation.mutateAsync(
        {
          newRequest: {
            ...quote.quoteRequest,
            transaction: {
              ...quote.quoteRequest.transaction,
              policyEffectiveDate: policyEffectiveDate,
              referralId: Math.floor(
                1000000000 + Math.random() * 9000000000
              ).toString(), // 10-digit random
              workItemId: null
            }
          },
          SelectionMetaData: { ...selectionMetaData, isPlanAdded: false }
        },
        {
          onSuccess: async (data) => {
            onUpdateQuote({
              ...data,
              quoteResponse: {
                ...data.quoteResponse,
                transactionDetails: {
                  ...data.quoteResponse.transactionDetails,
                  policyEffectiveDate
                }
              }
            });
            setOpen(false);
          }
        }
      );
    } catch (e) {
      alert("error");
    }
  };

  return (
    <div className="p-5 mt-4 bg-white border-b-4 rounded-md shadow-lg border-b-red-400">
      <img src="/assurant.png" alt="" />
      <p className="mt-4 text-sm text-gray-500">
        {isFullPay
          ? "Annual plan"
          : isQuarterlyPay
          ? "Quarterly plan"
          : "Economic plan"}
      </p>
      <div className="flex justify-between">
        <div>
          <div className="flex items-baseline mt-2">
            <p className="text-4xl font-light">
              {formatCurrency(
                isFullPay
                  ? Number(selectedPlan.downPaymentAmount) || 0
                  : Number(selectedPlan?.installmentAmount) +
                      Number(selectedPlan?.serviceFee)
              )}
            </p>
            <p>{isFullPay ? "/yr" : isQuarterlyPay ? "/quarter" : "/month"}</p>
          </div>
          {!isFullPay && (
            <p className="mt-2 text-sm text-blue-600">
              {formatCurrency(Number(selectedPlan?.downPaymentAmount))} down
              payment due today
            </p>
          )}
        </div>
        {isFullPay ? <img src="/best_value.svg" alt="Best value" /> : null}
      </div>
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
        <p>Payment plan</p>
        <Link to="payment-plan">
          <button className="flex items-center gap-2 text-sm underline">
            {isFullPay ? "Annual" : isQuarterlyPay ? "Quarterly" : "Economic"}
            <FontAwesomeIcon icon="chevron-right" />
          </button>
        </Link>
      </div>
      <div className="flex items-center justify-between mt-4">
        <p>Start date</p>
        <button
          className="flex items-center gap-2 text-sm underline"
          onClick={() => setOpen(true)}
        >
          {moment(
            quote.quoteResponse.transactionDetails.policyEffectiveDate
          ).format("MMMM D, YYYY")}
          <FontAwesomeIcon icon="chevron-right" />
        </button>
      </div>
      {isEffectiveDateInPast && (
        <div className="mt-1">
          <p className="text-sm text-red-600">
            Policy effective date must be in the future
          </p>
        </div>
      )}
      <Modal
        title="Select start date"
        open={open}
        onClose={() => setOpen(false)}
      >
        <form onSubmit={onSubmit}>
          <div className="py-4">
            <input
              type="date"
              name="policyEffectiveDate"
              className={textInputClass}
              defaultValue={defaultStartDate}
              min={moment().add(1, "day").format("yyyy-MM-DD")}
              max={max.format("yyyy-MM-DD")}
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="flex items-center gap-2"
              disabled={
                deleteAssuranceQuoteMutation.isLoading ||
                updateAssurantQuoteMutation.isLoading
              }
            >
              {(deleteAssuranceQuoteMutation.isLoading ||
                updateAssurantQuoteMutation.isLoading) && <Spinner />}
              Set date
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const getSelectedPlan = (quote: AssurantQuoteV2) => {
  const { transactionDetails } = quote.quoteResponse;
  const selectedNumberOfPayments = quote?.selectionMetaData?.numberOfPayments
    ? Number(quote?.selectionMetaData?.numberOfPayments)
    : 11;

  const selectedPlan =
    transactionDetails.payments.schedules.find(
      (s) => s.numberOfPayments === selectedNumberOfPayments
    ) ||
    transactionDetails.payments.schedules.find(
      (s) => s.numberOfPayments === 1
    ) ||
    transactionDetails.payments.schedules.find((s) => s.numberOfPayments === 4);

  return selectedPlan;
};

function UploadProof() {
  return (
    <div className="mt-8 mb-48 md:mb-28">
      <Link
        to={`../insurance/upload-proof`}
        state={{ label: "Insurance", returnTo: -1 }}
        className="flex items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
      >
        <FontAwesomeIcon
          icon={["far", "arrow-up-to-line"]}
          className="text-2xl text-red-400"
        />
        <div className="flex-1">
          <p className="font-medium">Already have insurance?</p>
          <p className="mt-1 text-sm">Provide proof of insurance</p>
        </div>
        <FontAwesomeIcon icon="chevron-right" className="text-gray-400" />
      </Link>
    </div>
  );
}

function AdditionallyInsured({
  user,
  quote,
  onUpdateQuote,
  selectionMetaData,
  refetch,
  updateMetaData
}: {
  user: User;
  quote: AssurantQuoteV2;
  onUpdateQuote: (newQuote: Partial<AssurantQuoteV2>) => void;
  selectionMetaData: SelectionMetaData | null;
  refetch: () => void;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
}) {
  const updateAssurantQuote = useUpdateInsuranceV2Quote(user);
  const roommates = user.roommates ?? [];
  const [selectedRoommate, setSelectedRoommate] = useState<number | null>(
    quote?.selectionMetaData?.additionalInsuredUserId || null
  );
  const [showRoommateModal, setShowRoommateModal] = useState(false);

  // Auto-include single roommate
  useEffect(() => {
    if (
      roommates.length === 1 &&
      !quote?.selectionMetaData?.additionalInsuredUserId
    ) {
      toggleAdditionalInsured(roommates[0].userId);
    }
  }, [roommates, quote?.selectionMetaData?.additionalInsuredUserId]);

  const toggleAdditionalInsured = async (userId: number | null) => {
    try {
      //@ts-ignore
      updateMetaData({
        ...selectionMetaData,
        additionalInsuredUserId: userId ?? -1,
        isPlanAdded: false
      });

      const updatedQuote = await updateAssurantQuote.mutateAsync({
        newRequest: null,
        SelectionMetaData: {
          ...selectionMetaData,
          additionalInsuredUserId: userId ?? -1,
          isPlanAdded: false
        }
      });
      onUpdateQuote(updatedQuote);
      setSelectedRoommate(userId);
      refetch();
    } catch (e) {
      console.error("Error updating additional insured:", e);
    }
  };

  const handleAddRoommate = () => {
    setShowRoommateModal(true);
  };

  const selectedFullName = roommates.find(
    (r) => String(r.userId) === String(selectedRoommate)
  )?.fullName;
  const shouldShowAddButton =
    roommates.length > 1 || [null, -1, "-1"].includes(selectedRoommate);
  return (
    <div className="mt-4">
      <p className="text-sm font-light text-gray-700">Additionally Insured</p>

      {/* Show selected roommate with option to remove */}
      {selectedRoommate && selectedFullName ? (
        <div className="flex items-center gap-2 mt-2">
          <p>{selectedFullName}</p>
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            onClick={() => toggleAdditionalInsured(-1)}
            disabled={updateAssurantQuote.isLoading}
          >
            <FontAwesomeIcon icon="times" />
          </button>
          {updateAssurantQuote.isLoading && <Spinner color="livly" />}
        </div>
      ) : null}

      {/* Add button if multiple roommates and none selected */}
      {shouldShowAddButton && (
        <button
          className="flex items-center gap-2 mt-2 text-sm text-red-300 underline"
          onClick={handleAddRoommate}
          disabled={updateAssurantQuote.isLoading}
        >
          <FontAwesomeIcon icon={["fal", "plus-circle"]} />
          Add additionally insured
        </button>
      )}

      {/* If only one roommate, and none selected, still show with remove */}
      {roommates.length === 1 &&
        !selectedRoommate &&
        quote?.selectionMetaData?.additionalInsuredUserId && (
          <div className="flex items-center gap-2 mt-2">
            <p>{roommates[0].fullName}</p>
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => toggleAdditionalInsured(null)}
              disabled={updateAssurantQuote.isLoading}
            >
              <FontAwesomeIcon icon="times" />
            </button>
            {updateAssurantQuote.isLoading && <Spinner color="livly" />}
          </div>
        )}

      {/* Roommate selection modal */}
      {showRoommateModal && (
        <Modal
          title="Select Roommate"
          open={showRoommateModal}
          onClose={() => setShowRoommateModal(false)}
        >
          <div className="py-4">
            {roommates.map((roommate) => (
              <div
                key={roommate.userId}
                className="flex items-center justify-between p-2 border-b"
              >
                <p>{roommate.fullName}</p>
                <Button
                  color="primary"
                  onClick={() => {
                    toggleAdditionalInsured(roommate.userId);
                    setShowRoommateModal(false);
                  }}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

function AddPhoneNumber() {
  const { user, updateUser } = useLivlyUser();
  const [open, setOpen] = useState(false);

  const updateUserMutation = useMutation((phoneNumber: string) =>
    updateLivlyUser(user.userId, user.status, phoneNumber, user.vybiconId)
  );

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const phoneNumber = formData.get("phone-number") as string;

    updateUserMutation.mutate(phoneNumber, {
      onSuccess: (data) => {
        setOpen(false);
        updateUser({
          phone: phoneNumber,
          unformattedPhone: data.phone
        });
      }
    });
  };

  return (
    <div className="flex justify-between">
      <button
        className="flex items-center gap-2 mt-2 text-sm text-red-300 underline"
        onClick={() => setOpen(true)}
      >
        <FontAwesomeIcon icon={["fal", "plus-circle"]} />
        Add phone number
      </button>
      <FontAwesomeIcon icon="exclamation-circle" className="text-red-500" />
      <Modal
        title="Add Phone Number"
        open={open}
        hideClose
        onClose={() => setOpen(false)}
      >
        <form method="post" onSubmit={onSubmit}>
          <fieldset disabled={updateUserMutation.isLoading}>
            <div className="relative py-8 mt-1">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                +1
              </div>
              <InputMask mask="(999) 999-9999" autoFocus>
                {
                  //@ts-ignore
                  (inputProps: any) => {
                    return (
                      <input
                        {...inputProps}
                        autoFocus
                        type="text"
                        name="phone-number"
                        id="phone-number"
                        className={classNames(
                          textInputClass,
                          "pl-10  shadow-none"
                        )}
                      />
                    );
                  }
                }
              </InputMask>
            </div>
            <div className="flex justify-end mt-4">
              <Button
                color="secondary"
                type="submit"
                className="flex items-center gap-2"
              >
                {updateUserMutation.isLoading ? <Spinner /> : null}
                Done
              </Button>
            </div>
          </fieldset>
        </form>
      </Modal>
    </div>
  );
}

function PlanActions({
  quote,
  isInsuranceOnly,
  selectionMetaData,
  updateMetaData,
  refetch,
  isRequire300kLiability,
  post300KAnswers
}: {
  quote: AssurantQuoteV2;
  isInsuranceOnly: boolean;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
  refetch: () => void;
  isRequire300kLiability: boolean;
  post300KAnswers: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useLivlyUser();
  const details = useGetStatusTypeDetails(OnboardingStatusEnum.insurance, user);

  const { updateOnboardingStatus } = usePostService(
    user.userId,
    user.propertyUnitLeaseId.toString(),
    details.onboardingStatus
  );
  const [status, setStatus] = useState<
    "adding" | "removing" | "keeping" | "pending"
  >("pending");
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);

  const onKeepPlan = async () => {
    if (isNativeAppVersion()) {
      returnToNative();
      return;
    }

    navigate("..");
  };

  const onAddPlan = async () => {
    try {
      setStatus("adding");
      if (isRequire300kLiability) {
        post300KAnswers();
      } else {
        refetch();
        const path = isRequire300kLiability
          ? "../insurance-quote/summary"
          : "../insurance-quote/liability-questions";
        const options =
          isInsuranceOnly === true
            ? { state: { isInsuranceOnly: true } }
            : undefined;
        navigate(path, options);
      }
    } catch (e) {
      setStatus("pending");
    }
  };

  const onRemovePlan = async () => {
    try {
      setStatus("removing");
      //@ts-ignore
      updateMetaData({
        ...selectionMetaData,
        isPlanAdded: false
      });

      await updateAssurantQuoteMutation.mutateAsync({
        newRequest: null,
        SelectionMetaData: {
          ...selectionMetaData,
          isPlanAdded: false
        }
      });
      refetch();
      const notes = "";
      const newOnboardingStatus: OnboardingStatus = {
        ...details.onboardingStatus,
        isCompleted: false,
        notes
      };
      await updateOnboardingStatus(newOnboardingStatus);
      queryClient.invalidateQueries(["insurance", "quote"]);

      if (isNativeAppVersion()) {
        returnToNative();
        return;
      }

      navigate("..");
    } catch (e) {
      setStatus("pending");
    }
  };

  const onDisagree = async () => {
    await deleteAssuranceQuoteMutation.mutateAsync();
    if (isNativeAppVersion()) {
      returnToNative();
      return;
    } else {
      navigate("..");
    }
  };

  const isNotValidPhone = !user.phone || !isValidPhone(user.phone);

  const isAddPlanDisabled =
    !quote?.selectionMetaData?.isPlanAdded && isNotValidPhone;

  const isLoading =
    updateAssurantQuoteMutation.isLoading || status !== "pending";

  return (
    <div className="max-w-4xl mx-auto fixed bottom-0 left-4 right-4 flex flex-col items-stretch m-4 bg-white border border-gray-100 rounded-lg shadow-lg md:flex-row md:justify-between md:items-baseline md:left-[272px] drop-shadow-lg">
      <div className="flex flex-col items-center justify-between w-full p-4 border-t border-gray-200 md:flex-row md:border-none">
        <div className="flex flex-col items-center justify-center mr-4 md:items-baseline">
          <p className="text-xs font-light text-center md:mr-2 md:text-left">
            I acknowledge that I have read and understand all applicable state
            disclosures, fraud notices, and notice of cancellation.
          </p>
        </div>
        <div className="flex items-center w-full gap-4 mt-6 md:mt-0 md:w-auto">
          {isInsuranceOnly ? (
            <>
              <button
                className="flex-1 w-full p-2 text-sm border border-gray-400 rounded-md md:flex-auto whitespace-nowrap"
                onClick={onDisagree}
              >
                Disagree
              </button>
              <Button
                color="secondary"
                className="flex-1 w-full md:flex-auto whitespace-nowrap"
                onClick={onAddPlan}
                disabled={isNotValidPhone || isLoading}
              >
                Add plan
              </Button>
            </>
          ) : quote?.selectionMetaData?.isPlanAdded ? (
            <>
              <Button
                color="default"
                className="flex-1 w-full md:flex-auto whitespace-nowrap"
                onClick={onKeepPlan}
                disabled={isLoading}
              >
                Keep Plan
              </Button>
              <Button
                color="secondary"
                onClick={onRemovePlan}
                disabled={isLoading}
                className="flex items-center flex-1 w-full gap-2 text-white bg-red-600 md:w-auto whitespace-nowrap hover:bg-red-700"
              >
                {isLoading && <Spinner />}
                {status === "removing" ? "Removing Plan" : "Remove Plan"}
              </Button>
            </>
          ) : (
            <>
              <button
                className="flex-1 w-full p-2 text-sm border border-gray-400 rounded-md md:flex-auto whitespace-nowrap"
                onClick={onDisagree}
              >
                Disagree
              </button>
              <Button
                color="secondary"
                onClick={onAddPlan}
                disabled={isAddPlanDisabled || isLoading}
                className="flex items-center flex-1 w-full gap-2 md:w-auto whitespace-nowrap"
              >
                {isLoading && <Spinner />}
                {status === "adding" ? "Adding Plan" : "Add Plan"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PolicyDelivery({
  user,
  quote,
  onUpdateQuote,
  selectionMetaData,
  updateMetaData,
  refetch
}: {
  user: User;
  quote: AssurantQuoteV2;
  onUpdateQuote: (newQuote: Partial<AssurantQuoteV2>) => void;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
  refetch: () => void;
}) {
  const [showElectronicConsent, setShowElectronicConsent] = useState(false);
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);

  const defaultLeaseAddress = user.addresses.find((a) => a.isLeaseAddress);
  let { billingAddress } =
    (quote?.selectionMetaData || selectionMetaData) ?? {};

  const isPaperless =
    quote?.selectionMetaData?.isPaperless === undefined ||
    quote?.selectionMetaData?.isPaperless === null
      ? true
      : quote?.selectionMetaData?.isPaperless;

  useEffect(() => {
    if (quote && user) {
      if (user.roommates && user.roommates.length === 1) {
        //@ts-ignore
        updateMetaData({
          ...selectionMetaData,
          additionalInsuredUserId: user.roommates[0].userId,
          isPlanAdded: false
        });
      }
    }
  }, [user, quote]);

  const mailingAddress = billingAddress?.id
    ? billingAddress
    : defaultLeaseAddress;

  const onSelectEmail = async () => {
    try {
      updateMetaData({
        ...selectionMetaData,
        isPaperless: true,
        //@ts-ignore
        billingAddress: { id: 0 },
        isPlanAdded: false
      });
      const result = await updateAssurantQuoteMutation.mutateAsync({
        newRequest: null,
        SelectionMetaData: {
          ...selectionMetaData,
          isPaperless: true,
          billingAddress: { id: 0 },
          isPlanAdded: false
        }
      });

      onUpdateQuote(result);
      refetch();
    } catch (e) {}
  };

  const onSelectMail = async () => {
    if (!mailingAddress) {
      return;
    }

    try {
      //@ts-expect-error
      updateMetaData({
        ...selectionMetaData,
        isPaperless: false,
        billingAddress: mailingAddress,
        isPlanAdded: false
      });
      const result = await updateAssurantQuoteMutation.mutateAsync({
        newRequest: null,
        SelectionMetaData: {
          ...selectionMetaData,
          isPaperless: false,
          billingAddress: mailingAddress,
          isPlanAdded: false
        }
      });

      onUpdateQuote(result);
      refetch();
    } catch (e) {}
  };

  // Fetch the electronic business consent disclosure
  const {
    data: electronicConsentDisclosure,
    isLoading: isElectronicConsentLoading
  } = useGetDisclosures(DisclosureType.ElectronicBusinessConsent, user.state);

  return (
    <div className="py-4">
      <h3 className="font-medium">Policy Delivery</h3>
      <p className="mt-2 text-sm">
        How would like to receive your policy and billing documents?
      </p>
      <div className="flex items-center gap-4 mt-4">
        <button
          className={classNames(
            { "border-red-400": isPaperless },
            "w-28 h-20 flex flex-col items-center justify-center rounded-lg border hover:bg-gray-50 transition duration-300 group"
          )}
          onClick={onSelectEmail}
          disabled={updateAssurantQuoteMutation.isLoading}
        >
          <FontAwesomeIcon
            icon={["fal", "envelope"]}
            className="w-6 h-6 text-red-400 duration-300 group-hover:scale-105"
          />
          <p className="mt-1 text-sm font-medium">Email</p>
        </button>
        {mailingAddress ? (
          <button
            className={classNames(
              { "border-red-400": !isPaperless },
              "w-28 h-20 flex flex-col items-center justify-center rounded-lg border hover:bg-gray-50 transition duration-300 group"
            )}
            onClick={onSelectMail}
            disabled={updateAssurantQuoteMutation.isLoading}
          >
            <FontAwesomeIcon
              icon={["fal", "mailbox"]}
              className="w-6 h-6 text-red-400 duration-300 group-hover:scale-105"
            />
            <p className="mt-1 text-sm font-medium">Mailbox</p>
          </button>
        ) : null}
        {updateAssurantQuoteMutation.isLoading ? (
          <Spinner color="livly" size="xl" />
        ) : null}
      </div>
      <div className="mt-4 text-sm">
        {isPaperless ? (
          <>
            <div className="mt-4">
              <p className="text-sm font-light text-gray-700">email address</p>
              <p className="mt-1">{user.email}</p>
            </div>
            <p className="mt-6 text-sm text-gray-700">
              By selecting this option, you agree to receive documents related
              to your policy electronically. These documents may include
              schedule of future payment notices, policy documents, among
              others. By clicking “Yes” I agree that I have read and accept the
              Electronic Business Consent.{" "}
              <button
                className="underline"
                onClick={() => setShowElectronicConsent(true)}
              >
                Electronic Business Consent.
              </button>
            </p>
          </>
        ) : (
          <Link
            className="block w-full p-4 text-left transition duration-300 border rounded-lg hover:bg-slate-50"
            to="address"
          >
            <div className="flex items-center justify-between">
              <div>
                <p>{mailingAddress?.address1}</p>

                {mailingAddress?.address2 && <p>{mailingAddress?.address2}</p>}

                <p>
                  {mailingAddress?.city}, {mailingAddress?.state}{" "}
                  {mailingAddress?.zipCode}
                </p>
              </div>
              <FontAwesomeIcon icon="chevron-right" />
            </div>
          </Link>
        )}
      </div>
      <Modal
        size="md"
        title=""
        open={showElectronicConsent}
        onClose={() => setShowElectronicConsent(false)}
      >
        {isElectronicConsentLoading ? (
          <div className="flex items-center justify-center min-h-[120px]">
            <Spinner color="livly" size="xl" />
          </div>
        ) : (
          <div
            className="prose max-w-none whitespace-pre-line text-sm text-gray-700"
            dangerouslySetInnerHTML={{
              __html:
                electronicConsentDisclosure?.Content ?? "No disclosure found."
            }}
          />
        )}
      </Modal>
    </div>
  );
}

async function updateLivlyUser(
  userId: number,
  status: string,
  phoneNumber: string,
  vybiconId?: string | null
) {
  const result = await axios.post<
    BaseLivlyApiResponse<{
      phone: string;
      status: string;
      userId: number;
      vybiconId: string | null;
    }>
  >(`${BASE_API_URL}/livly/users/me/update`, {
    phone: stripPhone(phoneNumber),
    userId,
    status,
    vybiconId
  });

  return result.data.Data;
}

export const useDeleteInsuranceV2Quote = (user: User) => {
  return useMutation(() =>
    deleteInsuranceQuote(user.userId, user.propertyUnitLeaseId)
  );
};

async function deleteInsuranceQuote(userId: number, leaseId: number) {
  const result = await axios.delete(
    `${BASE_API_URL}/v2/RentersInsurance/GH/Quote/${userId}/${leaseId}`,
    undefined
  );

  return result.data.Data;
}

async function getInsuranceQuote(
  userId: number,
  leaseId: number,
  metadata?: Record<string, any> | null
) {
  const result = await axios.post<BaseLivlyApiResponse<AssurantQuoteV2>>(
    `${BASE_API_URL}/v2/RentersInsurance/GH/Quote/${userId}/${leaseId}`,
    metadata
  );

  return result.data.Data;
}

export const insuranceQuoteQuery = (
  userId: number,
  leaseId: number,
  metadata: Record<string, any> | null = null
) => ({
  queryKey: ["insurance", "quote", "v2", userId, leaseId],
  queryFn: async () => getInsuranceQuote(userId, leaseId, metadata)
});

export const useInsuranceV2Quote = (
  user: User,
  selectionMetaData?: SelectionMetaData | null
) => {
  let metadata: Record<string, any> | null = {
    newRequest: null,
    SelectionMetaData: selectionMetaData ?? null
  };

  return useQuery({
    ...insuranceQuoteQuery(user.userId, user.propertyUnitLeaseId, metadata)
  });
};

export const useUpdateInsuranceV2Quote = (user: User) => {
  return useMutation((data: any) =>
    getInsuranceQuote(user.userId, user.propertyUnitLeaseId, data)
  );
};

async function requoteInsuranceQuote(
  userId: number,
  leaseId: number,
  metadata?: RequoteRequestModelV2
) {
  const result = await axios.post<BaseLivlyApiResponse<AssurantQuoteV2>>(
    `${BASE_API_URL}/v2/RentersInsurance/GH/Requote/${userId}/${leaseId}`,
    metadata
  );
  return result.data.Data;
}

export const useRequoteInsuranceV2Quote = (user: User) => {
  return useMutation((data: RequoteRequestModelV2) =>
    requoteInsuranceQuote(user.userId, user.propertyUnitLeaseId, data)
  );
};

const build300KLiabilityPayload = (
  liabilityQuestionsQuery: Questionnaire | null
) => {
  if (
    !liabilityQuestionsQuery ||
    !liabilityQuestionsQuery.questions ||
    liabilityQuestionsQuery.questions.length === 0
  ) {
    return null;
  }

  const firstQuestion = liabilityQuestionsQuery.questions[0];
  const yesOption = firstQuestion.options.find(
    (opt: any) => opt.optionValue === "Y"
  );

  if (!yesOption) {
    return null;
  }

  return {
    QuestionnaireId: liabilityQuestionsQuery.questionnaireId,
    Answers: [
      {
        QuestionId: firstQuestion.questionId,
        QuestionOptionId: yesOption.questionOptionId,
        TextAnswer: yesOption.optionValue
      }
    ]
  };
};

export default InsuranceQuoteV2;
